import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Mail, Phone, MapPin } from 'lucide-react';
import { contactInfo } from '../mock';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-6">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#dc143c] hover:text-[#b01030] transition-colors font-medium"
          >
            <ArrowLeft size={20} />
            <span>Back to Home</span>
          </Link>
        </div>

        {/* Header */}
        <div className="mb-12 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Shield size={48} className="text-[#dc143c]" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-[#1a1a2e] mb-4">
            Privacy Policy
          </h1>
          <p className="text-lg text-[#666]">
            Last Updated: January 2025
          </p>
        </div>

        {/* Content */}
        <div className="prose prose-lg max-w-none">
          <section className="mb-8">
            <h2 className="text-3xl font-bold text-[#1a1a2e] mb-4">Introduction</h2>
            <p className="text-[#666] leading-relaxed mb-4">
              Welcome to Bhargava's Shoes ("we," "our," or "us"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or interact with our business.
            </p>
            <p className="text-[#666] leading-relaxed">
              By using our website, you agree to the collection and use of information in accordance with this Privacy Policy. If you do not agree with our policies and practices, please do not use our website.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-bold text-[#1a1a2e] mb-4">Information We Collect</h2>
            
            <h3 className="text-2xl font-bold text-[#1a1a2e] mb-3 mt-6">Personal Information</h3>
            <p className="text-[#666] leading-relaxed mb-4">
              We may collect personal information that you voluntarily provide to us when you:
            </p>
            <ul className="list-disc pl-6 text-[#666] space-y-2 mb-4">
              <li>Fill out our contact form</li>
              <li>Subscribe to our newsletter</li>
              <li>Contact us via phone or email</li>
              <li>Visit our showroom and provide information</li>
            </ul>
            <p className="text-[#666] leading-relaxed mb-4">
              The personal information we collect may include:
            </p>
            <ul className="list-disc pl-6 text-[#666] space-y-2">
              <li>Name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Message content</li>
              <li>Any other information you choose to provide</li>
            </ul>

            <h3 className="text-2xl font-bold text-[#1a1a2e] mb-3 mt-6">Automatically Collected Information</h3>
            <p className="text-[#666] leading-relaxed mb-4">
              When you visit our website, we automatically collect certain information about your device, including:
            </p>
            <ul className="list-disc pl-6 text-[#666] space-y-2">
              <li>Browser type and version</li>
              <li>Operating system</li>
              <li>IP address</li>
              <li>Pages visited and time spent on pages</li>
              <li>Referring website addresses</li>
              <li>Date and time of visits</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-bold text-[#1a1a2e] mb-4">How We Use Your Information</h2>
            <p className="text-[#666] leading-relaxed mb-4">
              We use the information we collect in the following ways:
            </p>
            <ul className="list-disc pl-6 text-[#666] space-y-2">
              <li>To respond to your inquiries and provide customer service</li>
              <li>To send you updates about our products and services</li>
              <li>To improve our website and user experience</li>
              <li>To analyze website traffic and usage patterns</li>
              <li>To comply with legal obligations</li>
              <li>To prevent fraud and enhance security</li>
              <li>To communicate with you about promotions and special offers (with your consent)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-bold text-[#1a1a2e] mb-4">Cookies and Tracking Technologies</h2>
            <p className="text-[#666] leading-relaxed mb-4">
              We use cookies and similar tracking technologies to track activity on our website and store certain information. Cookies are files with a small amount of data that may include an anonymous unique identifier.
            </p>
            <p className="text-[#666] leading-relaxed mb-4">
              You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our website.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-bold text-[#1a1a2e] mb-4">Third-Party Services</h2>
            <p className="text-[#666] leading-relaxed mb-4">
              Our website uses the following third-party services:
            </p>
            
            <h3 className="text-2xl font-bold text-[#1a1a2e] mb-3 mt-6">Instagram Integration</h3>
            <p className="text-[#666] leading-relaxed mb-4">
              We display content from our Instagram account (@bhargavashoesbti) on our website. When you interact with this content, Instagram may collect information about you. Please refer to Instagram's Privacy Policy for more information.
            </p>

            <h3 className="text-2xl font-bold text-[#1a1a2e] mb-3 mt-6">Google Maps</h3>
            <p className="text-[#666] leading-relaxed mb-4">
              We use Google Maps to display our showroom location. Google may collect information when you use this feature. Please refer to Google's Privacy Policy for more information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-bold text-[#1a1a2e] mb-4">Data Security</h2>
            <p className="text-[#666] leading-relaxed mb-4">
              We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-bold text-[#1a1a2e] mb-4">Data Retention</h2>
            <p className="text-[#666] leading-relaxed mb-4">
              We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-bold text-[#1a1a2e] mb-4">Your Rights</h2>
            <p className="text-[#666] leading-relaxed mb-4">
              You have the following rights regarding your personal information:
            </p>
            <ul className="list-disc pl-6 text-[#666] space-y-2">
              <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Objection:</strong> Object to our processing of your personal information</li>
              <li><strong>Restriction:</strong> Request restriction of processing your personal information</li>
              <li><strong>Portability:</strong> Request transfer of your information to another service</li>
            </ul>
            <p className="text-[#666] leading-relaxed mt-4">
              To exercise any of these rights, please contact us using the information provided below.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-bold text-[#1a1a2e] mb-4">Children's Privacy</h2>
            <p className="text-[#666] leading-relaxed mb-4">
              Our website is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us, and we will delete such information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-bold text-[#1a1a2e] mb-4">Changes to This Privacy Policy</h2>
            <p className="text-[#666] leading-relaxed mb-4">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date at the top of this policy. You are advised to review this Privacy Policy periodically for any changes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-bold text-[#1a1a2e] mb-4">Contact Us</h2>
            <p className="text-[#666] leading-relaxed mb-6">
              If you have any questions about this Privacy Policy or our privacy practices, please contact us:
            </p>
            
            <div className="bg-[#f7f5f2] p-8 rounded-lg space-y-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-[#dc143c] rounded-sm flex items-center justify-center">
                  <MapPin size={20} className="text-white" />
                </div>
                <div>
                  <p className="text-sm font-medium text-[#666] uppercase tracking-wider mb-1">Address</p>
                  <p className="text-base text-[#1a1a2e] font-medium">{contactInfo.address}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-[#dc143c] rounded-sm flex items-center justify-center">
                  <Phone size={20} className="text-white" />
                </div>
                <div>
                  <p className="text-sm font-medium text-[#666] uppercase tracking-wider mb-1">Phone</p>
                  <a
                    href={`tel:${contactInfo.phone}`}
                    className="text-base text-[#1a1a2e] font-medium hover:text-[#dc143c] transition-colors"
                  >
                    {contactInfo.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-[#dc143c] rounded-sm flex items-center justify-center">
                  <Mail size={20} className="text-white" />
                </div>
                <div>
                  <p className="text-sm font-medium text-[#666] uppercase tracking-wider mb-1">Email</p>
                  <a
                    href={`mailto:${contactInfo.email}`}
                    className="text-base text-[#1a1a2e] font-medium hover:text-[#dc143c] transition-colors"
                  >
                    {contactInfo.email}
                  </a>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-bold text-[#1a1a2e] mb-4">Consent</h2>
            <p className="text-[#666] leading-relaxed">
              By using our website, you hereby consent to our Privacy Policy and agree to its terms.
            </p>
          </section>
        </div>

        {/* Back to Home Button */}
        <div className="mt-12 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-[#dc143c] text-white px-8 py-4 rounded-sm text-base font-medium hover:bg-[#b01030] transition-all duration-300"
          >
            <ArrowLeft size={20} />
            <span>Back to Home</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
